import React from 'react';
import { PageHero } from '../../components/ui/PageHero';
import { QuoteBanner } from '../../components/ui/QuoteBanner';
import { ReviewNote } from '../../components/ui/ReviewNote';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { ImageSourceBadge } from '../../components/ui/ImageSourceBadge';
import { useSeo } from '../../hooks/useSeo';
import { pageMeta } from '../../data/seo';
import { deliverySteps, projectRoles, qualityProcedures } from '../../data/quality';
import { images, heroImages } from '../../data/images';
export function QualityProjectManagement() {
  useSeo(pageMeta.quality);
  return <>
      <PageHero title="Quality assurance & project management" intro="How the company organises its project teams and controls quality from mobilisation to completion." image={heroImages.water} breadcrumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'About Us'
    }, {
      label: 'Quality Assurance & Project Management'
    }]} data-id="element-608" data-mp="1ofr3mbmy8sct" />

      <section aria-labelledby="approach-title" className="bg-charcoal py-20 md:py-24" data-id="element-609" data-mp="3fgmfzll5xqsf">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-610" data-mp="3ap4g70uvlw91">
          <SectionHeading id="approach-title" label="Approach" title="Staffing every project discipline" intro="The company's principle is to provide enough experienced professionals in each project discipline to finish the work within the contract time. A competent project team works alongside head office support staff, and site-based support staff are provided under the construction contract." className="max-w-3xl" data-id="element-611" data-mp="3anjjp5p0438j" />
          <ol className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8" data-id="element-612" data-mp="3pcpjovvnxxhh">
            {deliverySteps.map((step, index) => <li key={step.title} className="relative border-t-2 border-white/15 pt-6" data-id="element-613" data-mp="2dz3vpe1ji1xj" data-mpi={index}>
                <span className="absolute -top-[2px] left-0 h-[2px] w-16 bg-brand-red" aria-hidden="true" data-id="element-614" data-mp="290afhduvbwto" />
                <span className="font-display text-[15px] font-bold uppercase tracking-[0.16em] text-brand-light" data-id="element-615" data-mp="2ahpkwhjky2ik">Stage {index + 1}</span>
                <h3 className="mt-2 font-display text-[26px] font-bold uppercase leading-none text-white" data-id="element-616" data-mp="2ff7wygsw8xus">{step.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-617" data-mp="21ghv6nv4qa03">{step.text}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section aria-labelledby="roles-title" className="bg-charcoal-950 py-20 md:py-24" data-id="element-618" data-mp="1rpwzp9ar5lle">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-619" data-mp="1whyeb1ve7oel">
          <SectionHeading id="roles-title" label="Project management team" title="Roles and responsibilities" className="max-w-2xl" data-id="element-620" data-mp="1wjhp4cya578z" />
          <div className="mt-12 divide-y divide-white/15 border-y border-white/15" data-id="element-621" data-mp="1mr3pbpqriapg">
            {projectRoles.map(role => <article key={role.title} className="grid gap-6 py-10 lg:grid-cols-[380px_1fr] lg:gap-16" data-id="element-622" data-mp="18sm2rvclf494">
                <div data-id="element-623" data-mp="1dqdvgrl2aued">
                  <h3 className="font-display text-[30px] font-extrabold uppercase leading-[0.95] text-white" data-id="element-624" data-mp="1dt00okkhcxtf">{role.title}</h3>
                  <p className="mt-4 text-[16px] leading-relaxed text-steel-300" data-id="element-625" data-mp="p6tzsdi0eswv">{role.summary}</p>
                </div>
                <ul className="grid content-start gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-626" data-mp="1374qngjbk7i1">
                  {role.responsibilities.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-steel-100" data-id="element-627" data-mp="z6ksqaqx8zuu">
                      <span className="mt-2 h-2 w-2 shrink-0 bg-brand-red" aria-hidden="true" data-id="element-628" data-mp="1d1womlgtcwdc" />
                      {item}
                    </li>)}
                </ul>
              </article>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="qa-title" className="bg-charcoal py-20 md:py-24" data-id="element-629" data-mp="1hvhs2t16eijx">
        <div className="mx-auto grid max-w-site gap-14 px-5 md:px-8 lg:grid-cols-2 lg:items-center" data-id="element-630" data-mp="1vvshrsqr1r3i">
          <div data-id="element-631" data-mp="1qwz3vz7jvy2t">
            <SectionHeading id="qa-title" label="Quality assurance" title="Quality control procedures" data-id="element-632" data-mp="1qx8sbicy5x6e" />
            <p className="mt-6 text-[17px] leading-relaxed text-steel-200" data-id="element-633" data-mp="3lbmfyngrnxy7">
              The profile says that, over the years, the company has developed tried and tested procedures for controlling
              quality on its construction projects. These include training for site engineers and personnel that sets out
              the tasks and duties needed to control the work effectively:
            </p>
            <ul className="mt-6 grid gap-x-8 border-t border-white/15 sm:grid-cols-2" data-id="element-634" data-mp="32f3femk13id9">
              {qualityProcedures.map(item => <li key={item} className="border-b border-white/15 py-3 font-display text-[18px] font-semibold uppercase tracking-[0.03em] text-white" data-id="element-635" data-mp="3bd6y7b3mq11d">
                  {item}
                </li>)}
            </ul>
            <p className="mt-6 text-[17px] leading-relaxed text-steel-200" data-id="element-636" data-mp="qmoddzbsjtrl">
              Quality assurance is delivered through teamwork and peer-to-peer review. Every team member is expected to
              help produce an end product that meets the specification.
            </p>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden" data-id="element-637" data-mp="10spn0g3rgps7">
            <img src={images.surveyor.src} alt={images.surveyor.alt} loading="lazy" className="h-full w-full object-cover" data-id="element-638" data-mp="w064d4q7v4gj" />
            <ImageSourceBadge source={images.surveyor.source} className="absolute bottom-3 right-3" data-id="element-639" data-mp="ryb6693l176z" />
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-site px-5 md:px-8" data-id="element-640" data-mp="mxwvskeidl5s">
          <ReviewNote data-id="element-641" data-mp="hzvi6ezuqe1p">
            The profile does not mention any formal quality or safety certification (such as ISO 9001 or ISO 45001) or any
            safety performance statistics, so none are claimed here. If they exist, supply the certificates or a written
            QA/SHE policy so they can be added.
          </ReviewNote>
        </div>
      </section>

      <QuoteBanner data-id="element-642" data-mp="23ej2vtwc1oaq" />
    </>;
}